<template>
  <div class="tiles">
    <div class="tiles__filter-wrap" v-if="filterCategories && !seasons">
      <div class="tiles__filter">
        <div class="row">
          <div class="col-lg-4 mb-4 mb-lg-0">
            <div class="h3 mb-3 text-center">
              {{ _('mask-tiles.when') }}
            </div>
            <FakeSelect v-model="filters.when" :options="filterCategories.when"
                        :placeholder="_('mask-tiles.please-select')"
                        :selectedPlaceholder="_('mask-tiles.extend-selection')"/>
          </div>
          <div class="col-lg-4 mb-4 mb-lg-0">
            <div class="h3 mb-3 text-center">
              {{ _('mask-tiles.who') }}
            </div>
            <FakeSelect v-model="filters.who" :options="filterCategories.who"
                        :placeholder="_('mask-tiles.please-select')"
                        :selectedPlaceholder="_('mask-tiles.extend-selection')"/>
          </div>
          <div class="col-lg-4 mb-4 mb-lg-0">
            <div class="h3 mb-3 text-center">
              {{ _('mask-tiles.what') }}
            </div>
            <FakeSelect v-model="filters.what" :options="filterCategories.what"
                        :placeholder="_('mask-tiles.please-select')"
                        :selectedPlaceholder="_('mask-tiles.extend-selection')"/>
          </div>
        </div>
      </div>
    </div>

    <div class="tiles__filter-wrap" v-if="seasons">
      <div class="tiles__filter">
        <div class="row g-4">
          <div class="col-lg-3" v-for="season in seasons">
            <button @click.prevent="setSeason(season)"
                    :class="[filters.when.includes(season) ? 'button--secondary' : 'button--primary']"
                    class="button button--block">{{ season.label }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <template v-if="groupedTiles.length > 0">
      <Group v-for="(tiles, index) in groupedTiles" :filters="filters"
             :tiles="tiles" :even="index % 2 === 0"/>
    </template>
    <div class="row g-0 justify-content-center" v-else>
      <div class="col-12 col-md-6 col-lg-3 text-center fs-2">
        <em v-if="!{seasons}">{{ _('mask-tiles.no-results') }}</em>
        <em v-else>{{ _('mask-tiles.no-results-saison') }}</em>
      </div>
    </div>
  </div>
</template>


<script setup>
import {computed, ref, watch} from 'vue'
import Group from "./components/Group.vue";
import FakeSelect from "./components/FakeSelect.vue";

const props = defineProps({
  tiles: Array,
  adTiles: Array,
  filterCategories: Object,
  seasons: Object,
  seasonPreselect: Number,
  numTiles: Number,
  _: Function,
})

const filters = ref({
  when: [],
  who: [],
  what: [],
})

const anyFilters = ref(!props.filterCategories);

watch(filters, () => {
  anyFilters.value = Object.values(filters.value).some(value => value.length > 0) || !props.filterCategories;
}, {deep: true})

const setSeason = (season) => {
  filters.value.when = [season];
};

if (props.seasons && props.seasonPreselect) {
  setSeason(props.seasons.find(s => s.value === props.seasonPreselect));
}

const filteredTiles = computed(() => {

  const filteringIds = [
    ...filters.value.when,
    ...filters.value.who,
    ...filters.value.what,
  ].map(c => c.value)

  if (anyFilters.value) {
    return props.tiles.filter(tile => {
      let cats = tile.categories?.map(c => c.uid) ?? [];
      if (tile.mainCategory) {
        cats.push(tile.mainCategory.uid);
      }

      // all filteringIds must be in cats
      return filteringIds.every(id => cats.includes(id));
    });
  }

  return props.tiles;
});

function injectAds(tiles) {
  let tilesWithAds = [];
  let offset = 0;
  tiles.forEach((tile, i) => {
    tilesWithAds.push(tile);
    let bannerIndex = [0, 4, 6].indexOf(i + offset);
    if (bannerIndex > -1 && props.adTiles[bannerIndex] !== null) {
      tilesWithAds.push(props.adTiles[bannerIndex]);
      offset++;
    }
  });
  return tilesWithAds;
}


// Not invoked when filtering is active, only on "unfiltered" tiles
const groupedTiles = computed(() => {

  let tiles = filteredTiles.value;
  let highlights = tiles.filter(tile => tile.is_highlight);
  let nonHighlights = tiles.filter(tile => !tile.is_highlight);

  if (anyFilters.value) {
    if (props.seasons) {
      // randomize first, so all items get a chance to end up in the slice
      // (except if highlights is as big (or bigger) than the max number of
      //  tiles, then non-highlights get dropped)
      highlights.sort(() => Math.random() - 0.5);
      nonHighlights.sort(() => Math.random() - 0.5);
    }

    // [highligts] + [non-highlights] and slice to max numTiles
    tiles = highlights.concat(nonHighlights);
    tiles = tiles.slice(0, props.numTiles);

    if (props.seasons) {
      // another randomize, so highlights are not always first
      tiles.sort(() => Math.random() - 0.5);
    }

    tiles = injectAds(tiles);
    tiles = tiles.slice(0, props.numTiles);

    const grouped = []
    for (let i = 0; i < tiles.length; i += 4) {
      let group = tiles.slice(i, i + 4)
      if (group.length === 4 || grouped.length < 2) {
        grouped.push(group);
      }
    }
    return grouped
  } else {
    // just ensure highlights are first.
    tiles = highlights.concat(nonHighlights);
    tiles = injectAds(tiles);
    tiles = tiles.slice(0, props.numTiles);

    let defaultTiles = [];
    for (let i = 0; i <= props.numTiles; i += 4) {
      defaultTiles.push(tiles.slice(i, i + 4));
    }

    return defaultTiles;
  }
});

</script>
